/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { IProjectsAPI } from "../projects.service";
import * as Models from "../models";
import * as CSSO from "../../shared/types/csso.types";
import MockProjects from "./data/projects";
import MockPackages from "./data/packages";
import ErrorConstants from "../constants/error.constants";
import { MockAPISettings } from "./mock.types";
import { APIArgs, APIDetailsArgs } from "../api.types";

export class PersistentMockProjectsService implements IProjectsAPI {
  private projects: CSSO.Project[];
  private projectPackages: Record<string, CSSO.Package[]>;
  private projectPackagesById: Record<string, Record<string, CSSO.Package>>;
  public createNotFoundProjects: boolean;

  constructor(settings?: MockAPISettings) {
    this.createNotFoundProjects = false;
    if (settings?.createNotFoundProjects) {
      this.createNotFoundProjects = true;
    }

    if (settings?.overrideDataSet) {
      this.projects = settings.overrideDataSet;
    } else {
      this.projects = MockProjects.getMockProjects(
        settings?.projectCount ?? Math.floor(Math.random() * 300) + 1
      );
    }

    this.projectPackages = {};
    this.projectPackagesById = {};
    for (let i = 0; i < this.projects.length; ++i) {
      const proj = this.projects[i];
      const projectId = proj.id as string;

      if (!settings?.overrideDataSet) {
        if (
          proj.packages &&
          proj.packages.length < (settings?.minPackagesCount ?? 0)
        ) {
          proj.packages = MockPackages.getMockPackages(
            settings?.minPackagesCount ?? 0,
            proj.id
          );
        }
      }
      this.projectPackages[projectId] = proj.packages;
      this.projectPackagesById[projectId] = {};
      for (const pkg of proj.packages) {
        this.projectPackagesById[projectId][pkg.id] = pkg;
      }

      this.sortPackages(proj.packages);
    }
  }

  public async getProjects(
    args?: Models.GetProjectsParameters,
    REQUEST_TIME = 1000
  ): Promise<Models.GetProjectsResponse> {
    const limit = args?.limit ?? 12;
    const offset = args?.offset ?? 0;

    const mockLongRequest = (
      response: Models.GetProjectsResponse
    ): Promise<Models.GetProjectsResponse> => {
      const promise = new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(response);
        }, REQUEST_TIME);
      });
      return promise as Promise<Models.GetProjectsResponse>;
    };

    let isSearch = false;
    let count: number = limit;
    let projs: CSSO.Project[];

    if (args?.search) {
      projs = this.searchProjects(args.search, count, offset);
      isSearch = true;
    } else {
      projs = this.retrieveProjects(count, offset);
      isSearch = false;
    }

    const length = isSearch ? projs.length : this.projects.length;

    if (offset + limit > length) {
      if (offset <= length) {
        count = length - offset;
      } else {
        count = 0;
      }
    }

    const summaries: Models.ProjectSummary[] = [];
    for (const proj of projs) {
      const summary: Models.ProjectSummary = {
        projectId: proj.id,
        projectTitle: proj.projectTitle,
        siteReadyDate: proj.siteReadyDate,
        mainContact: proj.mainContact,
        projectCoordinator: proj.projectCoordinator ?? undefined,
        totalPackages: proj.packages.length,
        packages: [],
      };

      const status = PersistentMockProjectsService.FindProjectStatus(proj);
      if (status) {
        summary.status = { ...status };
      }

      const nextContact =
        PersistentMockProjectsService.FindProjectNextCEContact(proj);
      if (nextContact) {
        summary.nextCeContactTime = {
          timeAmount: nextContact.quantity,
          timeUnits: nextContact.units,
          dateTimeValue: undefined,
        };
      }

      for (let i = 0; i < proj.packages.length && i < 3; ++i) {
        const pkg = proj.packages[i];
        const pkgSummary: NonNullable<
          Required<Models.ProjectSummary>["packages"]
        >[number] = {
          packageId: pkg.id,
          packageName: pkg.name,
        };
        if (pkg.currentPhase >= 0) {
          pkgSummary.phase = {
            id: pkg.phases[pkg.currentPhase].id,
            name: pkg.phases[pkg.currentPhase].name,
          };

          pkgSummary.status = {
            id: pkg.phases[pkg.currentPhase].status?.id,
            name: pkg.phases[pkg.currentPhase].status?.name,
          };
        }
        summary.packages?.push(pkgSummary);
      }

      summaries.push(summary);
    }

    const result = await mockLongRequest({
      results: summaries,
      offset: args?.offset,
      limit: args?.limit,
      total: length,
      count: count,
      search: args?.search,
    });

    return result;
  }

  public async getProjectDetails(
    args: APIDetailsArgs
  ): Promise<Models.GetProjectDetailsResponse> {
    const project: CSSO.Project | null = this.retrieveProject(args.projectId);
    if (!project) {
      throw ErrorConstants.NOT_FOUND_ERROR;
    }

    const status = PersistentMockProjectsService.FindProjectStatus(project);
    const nextContact =
      PersistentMockProjectsService.FindProjectNextCEContact(project);

    const response: Models.GetProjectDetailsResponse = {
      projectId: project.id,
      projectTitle: project.projectTitle,
      siteReadyDate: project.siteReadyDate,
      mainContact: project.mainContact,
      projectCoordinator: project.projectCoordinator ?? undefined,
    };
    if (status) {
      response.status = { ...status };
    }
    if (nextContact) {
      response.nextCeContactTime = {
        timeAmount: nextContact.quantity,
        timeUnits: nextContact.units,
      };
    }
    return response;
  }

  public async getProjectPackages(
    args: APIArgs
  ): Promise<Models.GetProjectPackagesResponse> {
    if (args.projectId.length > 0) {
      const packages = this.retrievePackages(args.projectId);
      if (packages) {
        const response: Models.GetProjectPackagesResponse = {
          projectId: args.projectId,
          packages: [],
          totalPackages: packages.length,
        };

        for (const pkg of packages) {
          const pkgSummary: NonNullable<
            Required<Models.GetProjectPackagesResponse>["packages"]
          >[number] = {
            packageId: pkg.id,
            packageName: pkg.name,
          };
          if (pkg.currentPhase >= 0) {
            pkgSummary.phase = {
              id: pkg.phases[pkg.currentPhase].id,
              name: pkg.phases[pkg.currentPhase].name,
            };

            pkgSummary.status = {
              id: pkg.phases[pkg.currentPhase].status?.id,
              name: pkg.phases[pkg.currentPhase].status?.name,
            };
          }
          response.packages?.push(pkgSummary);
        }

        return response;
      }
    }

    throw ErrorConstants.BAD_REQUEST_ERROR;
  }

  public async getPackageDetails(
    args: APIDetailsArgs
  ): Promise<Models.GetPackageDetailsResponse> {
    const aPackage = this.retrievePackage(args.projectId, args.packageId);

    if (!aPackage) {
      throw ErrorConstants.BAD_REQUEST_ERROR;
    }

    const response: Models.GetPackageDetailsResponse = {
      projectId: args.projectId,
      packageId: args.packageId,
      packageName: aPackage.name,
      phases: [],
      status:
        PersistentMockProjectsService.GetPackagePhase(aPackage)?.status ??
        undefined,
      timing: {
        projectedEndDate: aPackage.timing.projectedEndDate ?? undefined,
        requestedByDate: aPackage.timing.requestedByDate ?? undefined,
        siteReadyDate: aPackage.timing.siteReadyDate ?? undefined,
        daysOffSchedule: aPackage.timing.daysOffSchedule ?? undefined,
        nextCeContact: {
          timeAmount: aPackage.timing.nextCeContact?.quantity ?? undefined,
          timeUnits: aPackage.timing.nextCeContact?.units ?? undefined,
          dateTimeValue: undefined,
        },
      },
      contacts: {
        coordinator: {
          id: aPackage.contacts.coordinator?.id ?? undefined,
          name: aPackage.contacts.coordinator?.name ?? undefined,
          email: aPackage.contacts.coordinator?.email ?? undefined,
        },
        mainContact: aPackage.contacts.mainContact ?? undefined,
      },
    };

    for (const phase of aPackage.phases) {
      const copy: NonNullable<
        Required<Models.GetPackageDetailsResponse>["phases"]
      >[0] = {
        phase: {
          id: phase.id,
          name: phase.name,
        },
        state: undefined,
        status: { ...(phase.status ?? undefined) },
        completionDate: phase.completionDate ?? undefined,
        sortOrder: phase.index,
      };
      response.phases?.push(copy);
    }

    if (response.phases) {
      for (let i = 0; i < aPackage.currentPhase; ++i) {
        if (response.phases[i]) {
          response.phases[i].state = {
            id: "complete",
            name: "Complete",
          };
        }
      }

      if (
        aPackage.currentPhase >= 0 &&
        aPackage.currentPhase < response.phases.length
      ) {
        if (response.phases[aPackage.currentPhase]) {
          response.phases[aPackage.currentPhase].state = {
            id: "active",
            name: "Active",
          };
        }
      }

      for (let i = aPackage.currentPhase + 1; i < response.phases.length; ++i) {
        if (response.phases[i]) {
          response.phases[i].state = {
            id: "pending",
            name: "Pending",
          };
        }
      }
    }

    return response;
  }

  public async getPackageNotifications(
    args: APIDetailsArgs
  ): Promise<Models.GetPackageNotificationsResponse> {
    const pkg = this.retrievePackage(args.projectId, args.packageId);
    if (!pkg) {
      throw ErrorConstants.NOT_FOUND_ERROR;
    }

    return {
      projectId: pkg.projectId,
      packageId: pkg.id,
      notifications: pkg.notifications.map((notif) => ({
        notificationId: notif.id,
        notificationType: notif.type,
        notificationDescription: notif.description,
        address: notif.address,
        county: notif.county,
        city: notif.city,
        status: notif.status ?? undefined,
      })),
    };
  }

  protected retrieveProjects(limit: number, offset: number): CSSO.Project[] {
    if (limit == 0) {
      return [];
    }

    if (this.projects.length > limit + offset) {
      return this.projects.slice(offset, offset + limit);
    }
    return this.projects.slice(offset);
  }

  protected retrieveProject(projectId: string): CSSO.Project | null {
    for (const project of this.projects) {
      if (project.id === projectId) {
        return project;
      }
    }
    if (this.createNotFoundProjects) {
      return this.addNewProject(projectId);
    }
    return null;
  }

  protected retrievePackages(projectId: string): Array<CSSO.Package> | null {
    let packages = this.projectPackages[projectId];
    if (packages) {
      return packages;
    } else if (this.createNotFoundProjects) {
      this.addNewProject(projectId);
      packages = this.projectPackages[projectId];
      if (packages) {
        return packages;
      }
    }
    return null;
  }

  protected retrievePackage(
    projectId: string,
    packageId: string
  ): CSSO.Package | null {
    let projectPackages = this.projectPackagesById[projectId];
    if (!projectPackages && this.createNotFoundProjects) {
      this.addNewProjectAndPackage(projectId, packageId);
      projectPackages = this.projectPackagesById[projectId];
    } else if (!projectPackages) {
      console.log("Didn't find projectPackages and not creating new data.");
    }
    let pkg = projectPackages?.[packageId] ?? null;
    if (!pkg && this.createNotFoundProjects) {
      pkg = this.addNewPackage(projectId, packageId);
    }

    return pkg;
  }

  protected getTotalProjects(): number {
    return this.projects.length;
  }

  protected searchProjects(
    query: string,
    count: number,
    offset: number
  ): CSSO.Project[] {
    const results: CSSO.Project[] = [];
    if (count <= 0) {
      return results;
    }

    if (query.length === 0) {
      throw ErrorConstants.BAD_REQUEST_ERROR;
    }

    let counter = offset;

    for (const proj of this.projects) {
      if (this.searchProject(query, proj)) {
        if (counter > 0) {
          counter--;
        } else {
          results.push(proj);
          if (results.length >= count) {
            break;
          }
        }
      }
    }

    return results;
  }

  protected searchProject(query: string, project: CSSO.Project): boolean {
    const upperQuery = query.toUpperCase();
    // Start at the topmost level and then descend through the
    // child levels in order to reduce the total amount of comparisons
    // needed
    if (project.projectTitle.toUpperCase().includes(upperQuery)) {
      return true;
    }
    if (project.mainContact?.toUpperCase().includes(upperQuery)) {
      return true;
    }
    for (const pkg of project.packages) {
      if (pkg.name.toUpperCase().includes(upperQuery)) {
        return true;
      }
    }
    for (const pkg of project.packages) {
      for (const notif of pkg.notifications) {
        if (notif.id.toUpperCase().includes(upperQuery)) {
          return true;
        } else if (notif.address?.includes(upperQuery)) {
          return true;
        }
      }
    }

    return false;
  }

  protected sortPackages(packages: CSSO.Package[]): void {
    packages.sort((x, y) => {
      if (x.timing?.siteReadyDate) {
        const xDate = new Date(x.timing.siteReadyDate);
        if (y.timing?.siteReadyDate) {
          const yDate = new Date(y.timing.siteReadyDate);
          if (xDate < yDate) {
            return -1;
          } else if (xDate == yDate) {
            return 0;
          } else {
            return 1;
          }
        } else {
          return -1;
        }
      } else if (y.timing?.siteReadyDate) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  protected addNewProject(projectId: string): CSSO.Project {
    const proj = MockProjects.createProject(projectId);
    this.projects.push(proj);

    this.projectPackages[projectId] = proj.packages;
    this.projectPackagesById[projectId] = {};
    for (const pkg of proj.packages) {
      this.projectPackagesById[projectId][pkg.id] = pkg;
    }
    this.sortPackages(proj.packages);
    return proj;
  }

  protected addNewPackage(projectId: string, packageId: string): CSSO.Package {
    const proj = this.retrieveProject(projectId)!;
    const newPkg = MockPackages.createPackage(projectId, packageId);
    proj?.packages.push(newPkg);
    this.projectPackagesById[projectId][newPkg.id] = newPkg;

    this.sortPackages(proj.packages);
    return newPkg;
  }

  protected addNewProjectAndPackage(
    projectId: string,
    packageId: string
  ): CSSO.Project {
    const proj = MockProjects.createProject(projectId);
    const newPkg = MockPackages.createPackage(projectId, packageId);
    this.projects.push(proj);

    this.projectPackages[projectId] = proj.packages;
    this.projectPackagesById[projectId] = {};
    for (const pkg of proj.packages) {
      this.projectPackagesById[projectId][pkg.id] = pkg;
    }
    this.projectPackagesById[projectId][newPkg.id] = newPkg;
    this.sortPackages(proj.packages);
    return proj;
  }

  protected static FindProjectStatus(
    project: CSSO.Project
  ): CSSO.Status | null {
    for (const pkg of project.packages) {
      if (pkg.currentPhase >= 0) {
        const phase = pkg.phases[pkg.currentPhase];
        if (phase.status) {
          return phase.status;
        }
      }
    }
    return null;
  }

  protected static FindProjectNextCEContact(
    project: CSSO.Project
  ): CSSO.TimeEstimate | null {
    for (const pkg of project.packages) {
      if (pkg.timing?.nextCeContact) {
        return pkg.timing.nextCeContact;
      }
    }
    return null;
  }

  // Helper to facilitate null-coercion syntax
  protected static GetPackagePhase(aPackage: CSSO.Package): CSSO.Phase | null {
    if (aPackage.currentPhase >= 0) {
      return aPackage.phases[aPackage.currentPhase];
    } else {
      return null;
    }
  }
}
